import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Phone, ArrowRight, CheckCircle2, ChevronRight, MapPin } from "lucide-react";
import { company } from "../mock";
import { getCombo } from "../comboPages";
import { serviceContent } from "../serviceContent";
import useSEO from "../hooks/useSEO";
import { BIZ_REF } from "../seo";
import ContactForm from "../components/ContactForm";
import FAQ from "../components/FAQ";
import JsonLd from "../components/JsonLd";

const ServiceCityPage = () => {
  const { serviceSlug, citySlug } = useParams();
  const combo = getCombo(serviceSlug, citySlug);

  useSEO({
    title: combo?.metaTitle,
    description: combo?.metaDesc,
    canonicalPath: combo?.canonicalPath,
    noindex: combo?.noindex,
    image: combo?.svc.image,
  });

  if (!combo) return <Navigate to={serviceContent[serviceSlug] ? `/services/${serviceSlug}` : "/services"} replace />;

  const { svc, area, place, serviceSlug: sSlug, citySlug: cSlug, zips, h1, intro, faqs, nearbyCities, otherServices } = combo;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://fabbuildpro.com/" },
      { "@type": "ListItem", position: 2, name: "Services", item: "https://fabbuildpro.com/services" },
      { "@type": "ListItem", position: 3, name: svc.name, item: `https://fabbuildpro.com/services/${sSlug}` },
      { "@type": "ListItem", position: 4, name: `${svc.name} in ${area.name}`, item: `https://fabbuildpro.com/services/${sSlug}/${cSlug}` },
    ],
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: svc.name,
    name: h1,
    provider: BIZ_REF,
    areaServed: { "@type": "Place", name: place },
    description: combo.metaDesc,
  };

  return (
    <>
      <JsonLd data={breadcrumbSchema} id="combo-breadcrumb" />
      <JsonLd data={serviceSchema} id="combo-service" />

      {/* Hero */}
      <section className="relative bg-brand-navy overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-brand-slate to-brand-navy" />
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-brand-orange/10 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 py-16 md:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="flex flex-wrap items-center gap-2 text-sm text-white/60 mb-4">
              <Link to="/" className="hover:text-brand-orange transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to={`/services/${sSlug}`} className="hover:text-brand-orange transition-colors">{svc.name}</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-brand-orange">{area.name}</span>
            </div>
            <p className="text-brand-orange font-semibold tracking-[0.18em] text-sm uppercase mb-3">
              {area.region}
            </p>
            <h1 className="font-display font-extrabold text-white text-3xl md:text-4xl leading-tight" data-testid="combo-h1">
              {h1}
            </h1>
            <p className="text-white/75 text-lg mt-5 max-w-xl leading-relaxed">
              Trusted, local {svc.name.toLowerCase()} for {area.name} homeowners. Free inspections, honest pricing, and work backed by our 100% satisfaction guarantee.
            </p>
            <div className="flex flex-wrap gap-3 mt-7">
              <a href={company.phoneHref} className="inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orangedark text-white font-semibold px-6 py-3.5 rounded-full shadow-lg transition-all hover:-translate-y-0.5" data-testid="combo-call-btn">
                <Phone className="w-5 h-5" /> Call {company.phone}
              </a>
              <Link to="/contact" className="inline-flex items-center gap-2 bg-white/5 border border-white/25 text-white font-semibold px-6 py-3.5 rounded-full hover:bg-white/10 transition-all" data-testid="combo-estimate-btn">
                Free Estimate <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
          <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-white/10">
            <img src={svc.image} alt={`${svc.name} in ${place}`} className="w-full h-[320px] object-cover" />
          </div>
        </div>
      </section>

      {/* Intro + benefits */}
      <section className="bg-brand-cream py-16">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-4 text-slate-700 leading-relaxed text-[17px]">
            <h2 className="font-display font-extrabold text-brand-navy text-2xl md:text-3xl mb-2">
              {svc.name} You Can Trust in {area.name}
            </h2>
            {intro.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          <div className="bg-white rounded-2xl border border-black/5 shadow-sm p-6 h-fit">
            <h2 className="font-display font-bold text-brand-navy text-lg mb-4">
              Why {area.name} chooses Fab Build Pro
            </h2>
            <ul className="space-y-3">
              {svc.benefits.map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-slate-700 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-brand-orange shrink-0" />
                  {b}
                </li>
              ))}
            </ul>
            <p className="text-xs text-slate-500 mt-4">
              Serving {area.name} & nearby ZIP codes: {zips}
            </p>
          </div>
        </div>
      </section>

      {/* Lead form */}
      <ContactForm />

      {/* FAQ */}
      <FAQ heading={`${svc.name} in ${area.name} — FAQs`} items={faqs} />

      {/* Other services in this city */}
      <section className="bg-brand-sand py-14">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="font-display font-extrabold text-brand-navy text-2xl mb-6">
            Other Services in {area.name}
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            {otherServices.map((r) => (
              <Link
                key={r.slug}
                to={`/services/${r.slug}/${cSlug}`}
                className="inline-flex items-center gap-1.5 bg-white border border-black/10 text-brand-navy rounded-full px-4 py-2 text-sm font-medium hover:bg-brand-orange hover:text-white hover:border-brand-orange transition-colors"
              >
                {r.name} in {area.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Nearby cities for this service */}
      <section className="bg-brand-cream py-16">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="font-display font-extrabold text-brand-navy text-2xl md:text-3xl mb-8">
            {svc.name} in Nearby Areas
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            {nearbyCities.map((n) => (
              <Link
                key={n.slug}
                to={`/services/${sSlug}/${n.slug}`}
                className="inline-flex items-center gap-1.5 bg-white border border-black/10 text-brand-navy rounded-full px-4 py-2 text-sm font-medium hover:bg-brand-orange hover:text-white hover:border-brand-orange transition-colors"
              >
                <MapPin className="w-3.5 h-3.5" />
                {svc.name} in {n.name}
              </Link>
            ))}
          </div>
          <Link
            to={`/service-areas/${cSlug}`}
            className="inline-flex items-center gap-2 mt-8 text-brand-orange font-semibold hover:gap-3 transition-all"
          >
            All services in {area.name} <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-brand-navy py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display font-extrabold text-white text-2xl md:text-3xl mb-4">
            {svc.name} in {place}
          </h2>
          <p className="text-white/70 mb-8 max-w-xl mx-auto">
            Speak with our local team about your project. Free inspections and honest, upfront pricing.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href={company.phoneHref} className="inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orangedark text-white font-semibold px-6 py-3.5 rounded-full shadow-lg transition-all hover:-translate-y-0.5">
              <Phone className="w-5 h-5" /> Call {company.phone}
            </a>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-white/5 border border-white/25 text-white font-semibold px-6 py-3.5 rounded-full hover:bg-white/10 transition-all">
              Request a Free Estimate <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default ServiceCityPage;
